type WebAudioWindow = Window & {
  webkitAudioContext?: typeof AudioContext;
};

let sharedCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const AC =
    window.AudioContext ?? (window as WebAudioWindow).webkitAudioContext;
  if (!AC) return null;
  if (!sharedCtx || sharedCtx.state === 'closed') {
    sharedCtx = new AC();
  }
  return sharedCtx;
}

/** Resume the shared context on a user gesture so a later mismatch can beep. */
export function unlockAmountMismatchAudio(): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    void ctx.resume().catch(() => undefined);
  }
}

function tone(
  ctx: AudioContext,
  time: number,
  frequency: number,
  duration: number,
  peak: number,
): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(frequency, time);
  gain.gain.setValueAtTime(0.0001, time);
  gain.gain.exponentialRampToValueAtTime(peak, time + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(time);
  osc.stop(time + duration + 0.03);
}

/** Two-tone attention chime + optional haptic. Fails silently if autoplay is blocked. */
export function playAmountMismatchAlert(): void {
  if (typeof document !== 'undefined' && document.hidden) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const start = () => {
    const now = ctx.currentTime;
    tone(ctx, now, 880, 0.1, 0.16);
    tone(ctx, now + 0.15, 1175, 0.18, 0.14);
    try {
      navigator.vibrate?.([70, 50, 90]);
    } catch {
      // Vibration is optional (desktop / permission denied).
    }
  };

  if (ctx.state === 'suspended') {
    void ctx.resume().then(start).catch(() => undefined);
  } else {
    start();
  }
}
